import React from 'react';
import { Descriptions, Drawer, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FC120Detail, FC121Detail } from '../../services/c514Api';

const { Text } = Typography;

function renderAbnormalReason(code: string): React.ReactNode {
  if (code === 'P') return <Tag color="orange">單價異常</Tag>;
  if (code === 'Q') return <Tag color="blue">數量超交</Tag>;
  if (code === 'L') return <Tag color="red">單價異常及數量超交</Tag>;
  return <Tag color="default">{code || '—'}</Tag>;
}

function renderApprovalStatus(stat: string): React.ReactNode {
  if (stat === 'D') return <Tag color="orange">待核准</Tag>;
  if (stat === 'P') return <Tag color="green">已核准</Tag>;
  if (stat === 'N') return <Tag color="red">已駁回</Tag>;
  return <Tag color="default">{stat || '—'}</Tag>;
}

function formatNumber(value: number | null | undefined, decimals = 2): string {
  if (value == null) return '—';
  return value.toLocaleString('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString('zh-TW');
  } catch {
    return date;
  }
}

interface C514DetailDrawerProps {
  visible: boolean;
  loading: boolean;
  data: FC120Detail | null;
  onClose: () => void;
}

export const detailColumns: ColumnsType<FC121Detail> = [
  { title: <span data-contract-field="fc121_02">項次</span>, dataIndex: 'fc121_02', key: 'seq', width: 50, align: 'center' },
  { title: <span data-contract-field="fc121_11">產品代號</span>, dataIndex: 'fc121_11', key: 'product', width: 120 },
  { title: <span data-contract-field="fc121_12">色號</span>, dataIndex: 'fc121_12', key: 'color', width: 80 },
  { title: <span data-contract-field="fc121_22">顏色名稱</span>, dataIndex: 'fc121_22', key: 'color_name', width: 140, ellipsis: true },
  { title: <span data-contract-field="fc121_13">規格</span>, dataIndex: 'fc121_13', key: 'spec', ellipsis: true },
  { title: <span data-contract-field="fc121_17">單位</span>, dataIndex: 'fc121_17', key: 'unit', width: 60, align: 'center' },
  {
    title: <span data-contract-field="fc121_21">S/C單價</span>,
    dataIndex: 'fc121_21',
    key: 'sc_price',
    width: 90,
    align: 'right',
    render: (v: number) => formatNumber(v, 3),
  },
  {
    title: <span data-contract-field="fc121_18">交運單價</span>,
    dataIndex: 'fc121_18',
    key: 'do_price',
    width: 90,
    align: 'right',
    render: (v: number) => formatNumber(v, 3),
  },
  {
    title: <span data-contract-field="fc121_20">總訂單量</span>,
    dataIndex: 'fc121_20',
    key: 'total_order_qty',
    width: 90,
    align: 'right',
    render: (v: number) => formatNumber(v),
  },
  {
    title: <span data-contract-field="fc121_16">交運數量</span>,
    dataIndex: 'fc121_16',
    key: 'do_qty',
    width: 90,
    align: 'right',
    render: (v: number) => formatNumber(v),
  },
  {
    title: <span data-contract-field="fc121_19">交運金額</span>,
    dataIndex: 'fc121_19',
    key: 'amount',
    width: 100,
    align: 'right',
    render: (v: number) => formatNumber(v),
  },
];

const C514DetailDrawer: React.FC<C514DetailDrawerProps> = ({ visible, loading, data, onClose }) => (
  <Drawer
    title={data ? `異常交運單明細 — ${data.fc120_01}` : '異常交運單明細'}
    placement="right"
    width={900}
    open={visible}
    onClose={onClose}
  >
    {loading ? (
      <Spin tip="載入中..." style={{ display: 'block', marginTop: 100 }} />
    ) : data ? (
      <>
        <div className="c514-detail-section">
          <h4>單頭資訊</h4>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="交運單號">{data.fc120_01}</Descriptions.Item>
            <Descriptions.Item label="S/C No">{data.fc120_10}</Descriptions.Item>
            <Descriptions.Item label="交運日期">{formatDate(data.fc120_11)}</Descriptions.Item>
            <Descriptions.Item label="客戶代號">{data.fc120_12}</Descriptions.Item>
            <Descriptions.Item label="部門代號">{data.fc120_17}</Descriptions.Item>
            <Descriptions.Item label="幣別">{data.fc120_18}</Descriptions.Item>
            <Descriptions.Item label="未稅金額">{formatNumber(data.fc120_20)}</Descriptions.Item>
            <Descriptions.Item label="含稅總額">{formatNumber(data.fc120_201)}</Descriptions.Item>
            <Descriptions.Item label="稅額">{formatNumber(data.fc120_16)}</Descriptions.Item>
            <Descriptions.Item label="明細行數">{data.fc120_27}</Descriptions.Item>
            <Descriptions.Item label="異常原因">{renderAbnormalReason(data.fc120_24)}</Descriptions.Item>
            <Descriptions.Item label="審核狀態">{renderApprovalStatus(data.fc120_80)}</Descriptions.Item>
          </Descriptions>
        </div>
        <div className="c514-detail-section">
          <h4>產品明細 ({data.details?.length || 0} 項)</h4>
          <Table<FC121Detail>
            rowKey={(r) => `${r.fc121_01}-${r.fc121_02}`}
            columns={detailColumns}
            dataSource={data.details || []}
            size="small"
            pagination={false}
            scroll={{ x: 900 }}
          />
        </div>
      </>
    ) : (
      <>
        <Text type="secondary">目前沒有可載入單據；以下顯示明細欄位結構供驗收。</Text>
        <Table<FC121Detail>
          rowKey={(r) => `${r.fc121_01}-${r.fc121_02}`}
          columns={detailColumns}
          dataSource={[]}
          size="small"
          pagination={false}
          scroll={{ x: 900 }}
          style={{ marginTop: 12 }}
        />
      </>
    )}
  </Drawer>
);

export default C514DetailDrawer;
